import { Slider, Stack, Text } from "@chakra-ui/react";
import * as React from "react";

import { applyConversionActionTemplates } from "../api/conversion-action";
import { useLoading, useToaster } from "../hooks";
import { brand } from "../lib/brand";
import { Button } from "./button";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "./dialog";
import { FacebookAdAccountField } from "./facebook-ad-account-field";
import { FacebookCapiCredentialsFields } from "./facebook-capi-credentials-fields";
import { Field } from "./field";
import { toaster } from "./toaster";

interface ConversionActionTemplatesDialogProps {
  isOpen: boolean;
  onOpenChange: (details: { open: boolean }) => void;
  onSuccess: () => void;
  workspaceSlug: string;
}

export function ConversionActionTemplatesDialog({
  isOpen,
  onOpenChange,
  onSuccess,
  workspaceSlug,
}: ConversionActionTemplatesDialogProps) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [adAccount, setAdAccount] = React.useState<string>("");
  const [dataset, setDataset] = React.useState<string>("");
  const [accessToken, setAccessToken] = React.useState<string>("");
  const [maxConversionsPercentile, setMaxConversionsPercentile] =
    React.useState<number>(25);
  const [isLoading, withLoading] = useLoading();
  const withToaster = useToaster();

  const resetState = () => {
    setAdAccount("");
    setDataset("");
    setAccessToken("");
    setMaxConversionsPercentile(25);
  };

  const canSubmit =
    adAccount !== "" && dataset !== "" && accessToken.trim() !== "";

  const handleSubmit = async () => {
    await withLoading(async () => {
      try {
        await withToaster(async () => {
          const created = await applyConversionActionTemplates(workspaceSlug, {
            "dataset-id": dataset,
            "access-token": accessToken.trim(),
            "max-conversions-percentile": maxConversionsPercentile,
          });
          toaster.create({
            type: "success",
            title: "Templates set up",
            description: `Created ${created.length} conversion events.`,
          });
          resetState();
          onOpenChange({ open: false });
          onSuccess();
        });
      } catch (error) {
        const axiosError = error as {
          response?: { data?: { error?: { message?: string } } };
          message?: string;
        };
        toaster.create({
          type: "error",
          title: "Failed to set up templates",
          description:
            axiosError?.response?.data?.error?.message ||
            axiosError?.message ||
            "An error occurred while setting up templates.",
        });
      }
    });
  };

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={(details) => {
        if (!details.open) resetState();
        onOpenChange(details);
      }}
      size="md"
    >
      <DialogContent ref={ref}>
        <DialogHeader>
          <DialogTitle>Set up conversion event templates</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Stack gap="4">
            <Text fontSize="sm" color="fg.muted">
              We&rsquo;ll create five {brand.productName} conversion events on
              this dataset: Predicted Revenue, Value-Based Optimization, Max
              Conversions Optimization, Whale, and Guppy.
            </Text>
            <FacebookAdAccountField
              workspaceSlug={workspaceSlug}
              value={adAccount}
              onChange={setAdAccount}
              portalRef={ref as React.RefObject<HTMLElement>}
            />
            {adAccount && (
              <FacebookCapiCredentialsFields
                workspaceSlug={workspaceSlug}
                adAccountId={adAccount}
                dataset={dataset}
                onDatasetChange={setDataset}
                accessToken={accessToken}
                onAccessTokenChange={setAccessToken}
                portalRef={ref as React.RefObject<HTMLElement>}
              />
            )}
            <Field
              label={`Max Conversions threshold: P${maxConversionsPercentile}`}
              helperText="Predictions strictly above this percentile fire the Max Conversions event."
            >
              <Slider.Root
                width="full"
                min={1}
                max={99}
                step={1}
                value={[maxConversionsPercentile]}
                onValueChange={({ value }) =>
                  setMaxConversionsPercentile(value[0])
                }
              >
                <Slider.Control>
                  <Slider.Track>
                    <Slider.Range />
                  </Slider.Track>
                  <Slider.Thumb index={0}>
                    <Slider.HiddenInput />
                  </Slider.Thumb>
                </Slider.Control>
              </Slider.Root>
            </Field>
          </Stack>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange({ open: false })}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || isLoading}
            loading={isLoading}
          >
            Set up templates
          </Button>
        </DialogFooter>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  );
}
